import { calculateAge, getTodayDateStringInBusinessTZ } from './date';

// How many days ahead to surface upcoming birthdays so classes can prepare.
export const DEFAULT_BIRTHDAY_LOOKAHEAD_DAYS = 7;

/** Minimal student shape needed to compute upcoming birthdays. */
export interface BirthdayInput {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
}

export interface BirthdayStudent {
  id: string;
  name: string;
  /** Age the student turns on this birthday, or null if DOB can't be parsed. */
  turnsAge: number | null;
  /** 0 = today, 1 = tomorrow, … up to the lookahead window. */
  daysAway: number;
  /** Month + day of the upcoming birthday, e.g. "May 9". */
  dateLabel: string;
}

/**
 * Returns the students whose birthday falls within the next `lookaheadDays`
 * (inclusive of today), sorted soonest first.
 *
 * Matching is done on the MM-DD portion of the DATE-typed `date_of_birth`
 * against a window anchored to today in the business timezone. Keying on
 * MM-DD handles month/year wrap (e.g. Dec 30 → Jan 2) and needs no timezone
 * conversion of the date value.
 */
export function getUpcomingBirthdays(
  students: BirthdayInput[],
  lookaheadDays: number = DEFAULT_BIRTHDAY_LOOKAHEAD_DAYS,
): BirthdayStudent[] {
  const [ty, tm, td] = getTodayDateStringInBusinessTZ().split('-').map(Number);
  const windowByMonthDay = new Map<string, { daysAway: number; dateLabel: string }>();
  for (let offset = 0; offset <= lookaheadDays; offset++) {
    const d = new Date(ty, tm - 1, td + offset);
    const mmdd = `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    // d is built from business-TZ y/m/d numbers, so its local month/day are the
    // correct calendar values to format (e.g. "May 9").
    if (!windowByMonthDay.has(mmdd)) {
      windowByMonthDay.set(mmdd, {
        daysAway: offset,
        dateLabel: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      });
    }
  }

  return students
    .map((s) => {
      const match = s.date_of_birth
        ? windowByMonthDay.get(s.date_of_birth.slice(5)) // MM-DD
        : undefined;
      if (match === undefined) return null;
      // calculateAge returns the current age. On the birthday itself it has
      // already ticked over; for a future day they turn one year older.
      const currentAge = calculateAge(s.date_of_birth);
      const turnsAge =
        typeof currentAge === 'number'
          ? match.daysAway === 0
            ? currentAge
            : currentAge + 1
          : null;
      return {
        id: s.id,
        name: `${s.first_name} ${s.last_name}`.trim(),
        turnsAge,
        daysAway: match.daysAway,
        dateLabel: match.dateLabel,
      };
    })
    .filter((s): s is BirthdayStudent => s !== null)
    .sort((a, b) => a.daysAway - b.daysAway);
}

export function birthdayWhenLabel(student: BirthdayStudent): string {
  if (student.daysAway === 0) return 'today';
  if (student.daysAway === 1) return 'tomorrow';
  return student.dateLabel;
}
